import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldCheck, Check, X, Clock, Inbox } from "lucide-react";

export default function AuthorizationRequests() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/transactions/change-requests", { params: { status_filter: statusFilter } });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const act = async (req, verb) => {
    setBusyId(req.id);
    try {
      await api.post(`/transactions/change-requests/${req.id}/${verb}`);
      toast.success(verb === "approve" ? "Richiesta approvata ed applicata" : "Richiesta rifiutata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="authorization-requests-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red"><ShieldCheck className="h-3 w-3" /> AUTORIZZAZIONI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Richieste autorizzazione</h1>
          <p className="text-zinc-400 mt-1">Approva o rifiuta le richieste di annullamento/modifica movimenti dal negozio.</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="auth-filter" className="bg-zinc-950 border-zinc-800 w-full sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">In attesa</SelectItem>
            <SelectItem value="approved">Approvate</SelectItem>
            <SelectItem value="rejected">Rifiutate</SelectItem>
            <SelectItem value="all">Tutte</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.length === 0 && (
          <div className="col-span-full text-center py-12 text-zinc-500">
            <Inbox className="h-12 w-12 mx-auto mb-2 opacity-40" />
            Nessuna richiesta {statusFilter === "pending" ? "in attesa" : ""}.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="card-elev p-4 space-y-3" data-testid={`auth-req-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading font-bold">
                  {r.action === "cancel" ? "🚫 Annullamento" : "✏️ Modifica"}
                </div>
                <div className="text-xs text-zinc-500">
                  Da <span className="text-zinc-200">{r.requested_by_name || "—"}</span>
                  {" · "}{new Date(r.created_at).toLocaleString("it-IT")}
                </div>
              </div>
              <span className={`pill ${r.status === "pending" ? "pill-gold" : r.status === "approved" ? "pill-mint" : "pill-muted"}`}>
                {r.status === "pending" ? <><Clock className="h-3 w-3" /> In attesa</> :
                 r.status === "approved" ? <><Check className="h-3 w-3" /> Approvata</> :
                 <><X className="h-3 w-3" /> Rifiutata</>}
              </span>
            </div>

            {r.transaction && (
              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-xs space-y-1">
                <div><span className="text-zinc-500">Cliente:</span> <span className="font-medium">{r.customer_name || "-"}</span> <span className="text-zinc-500 font-mono">{r.customer_card || ""}</span></div>
                <div><span className="text-zinc-500">Tipo:</span> {r.transaction.type}</div>
                <div><span className="text-zinc-500">Importo:</span> <span className="font-mono">€ {(r.transaction.amount_eur || 0).toFixed(2)}</span></div>
                <div><span className="text-zinc-500">Punti:</span> <span className="font-mono">{r.transaction.points_delta}</span></div>
              </div>
            )}

            {r.action === "modify" && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs space-y-1">
                <div className="text-amber-300 font-semibold">Modifiche richieste</div>
                {r.new_amount != null && <div>Nuovo importo: <span className="font-mono">€ {r.new_amount.toFixed(2)}</span></div>}
                {r.new_note != null && <div>Nuova nota: <span className="italic">"{r.new_note}"</span></div>}
              </div>
            )}

            {r.reason && <div className="text-xs text-zinc-400 italic">Motivo: "{r.reason}"</div>}

            {r.status === "pending" && (
              <div className="flex gap-2 pt-2 border-t border-zinc-900">
                <Button
                  data-testid={`auth-approve-${r.id}`}
                  size="sm"
                  onClick={() => act(r, "approve")}
                  disabled={busyId === r.id}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Check className="h-3.5 w-3.5 mr-1" /> Approva
                </Button>
                <Button
                  data-testid={`auth-reject-${r.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => act(r, "reject")}
                  disabled={busyId === r.id}
                  className="flex-1 border-zinc-800 hover:border-red-500 hover:text-red-300"
                >
                  <X className="h-3.5 w-3.5 mr-1" /> Rifiuta
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
